import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGDLData } from './useGDLData';
import { Badge } from './GDLComponents';

const API = process.env.REACT_APP_BACKEND_URL || '';
import axios from 'axios';

export default function GDLInvest() {
  const nav = useNavigate();
  const { funds, investments, csId, refresh } = useGDLData();
  const [selFund, setSelFund] = useState('');
  const [amt, setAmt] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  if (!csId) { nav('/demo/gdl/login'); return null; }

  const investable = (funds || []).filter(f => f.investable);
  const info = (funds || []).filter(f => !f.investable);

  const doInvest = async () => {
    setLoading(true); setMsg('');
    try {
      await axios.post(`${API}/api/demo/gdl/invest/${csId}`, { fund_name: selFund, amount: parseFloat(amt) }, { withCredentials: true });
      setMsg('Investment successful!'); setAmt(''); refresh();
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  const doRedeem = async (id) => {
    try { await axios.post(`${API}/api/demo/gdl/redeem/${id}`, {}, { withCredentials: true }); refresh(); }
    catch (e) { setMsg(e.response?.data?.detail || 'Redemption failed'); }
  };

  return (
    <div style={{ padding: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#2D0A10', letterSpacing: '-0.02em' }}>Invest</div>
        <div style={{ fontSize: 14, color: '#A07078', marginTop: 2 }}>Grow your wealth with GDL funds</div>
      </div>

      {/* Fund Cards */}
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10', marginBottom: 10 }}>Asset Management Funds</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {investable.map(f => (
            <button key={f.name} onClick={() => setSelFund(f.name)} data-testid={`fund-${f.name}`}
              style={{
                textAlign: 'left', padding: '18px', borderRadius: 18, cursor: 'pointer',
                border: selFund === f.name ? '2px solid #B03140' : 'none',
                background: selFund === f.name ? 'rgba(176,49,64,0.04)' : '#fff',
                boxShadow: '0 2px 10px rgba(176,49,64,0.06)',
                transition: 'all 0.15s ease',
              }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10' }}>{f.name}</div>
              <div style={{ fontSize: 13, color: '#A07078', marginTop: 2 }}>{f.tagline}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                <Badge variant="primary">{f.rate} p.a.</Badge>
                <Badge variant="grey">Min: {'\u20A6'}{f.min_investment?.toLocaleString()}</Badge>
                <Badge variant={f.risk === 'Low' ? 'success' : f.risk === 'Medium' ? 'warning' : 'error'}>{f.risk} risk</Badge>
              </div>
            </button>
          ))}
        </div>
        {selFund && (
          <div style={{ marginTop: 12 }}>
            <input type="number" value={amt} onChange={e => setAmt(e.target.value)} placeholder="Amount to invest"
              style={inp} data-testid="invest-amount" />
            {/* Live interest calculator */}
            {amt && parseFloat(amt) > 0 && (() => {
              const fund = investable.find(f => f.name === selFund);
              const rate = fund ? parseFloat(fund.rate) / 100 : 0;
              const amount = parseFloat(amt);
              const m3 = amount * rate * (3/12);
              const m6 = amount * rate * (6/12);
              const y1 = amount * rate;
              return (
                <div data-testid="interest-preview" style={{
                  marginTop: 12, background: 'rgba(176,49,64,0.04)', borderRadius: 16, padding: '16px 18px',
                  border: '1px solid rgba(176,49,64,0.08)',
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#8E2233', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>
                    Projected Returns at {fund?.rate} p.a.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { label: '3 months', interest: m3, total: amount + m3 },
                      { label: '6 months', interest: m6, total: amount + m6 },
                      { label: '12 months', interest: y1, total: amount + y1 },
                    ].map(p => (
                      <div key={p.label} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 14px', background: '#fff', borderRadius: 12,
                      }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#2D0A10' }}>{p.label}</div>
                          <div style={{ fontSize: 11, color: '#10B981', fontWeight: 600, marginTop: 1 }}>+{'\u20A6'}{p.interest.toLocaleString(undefined, { maximumFractionDigits: 0 })} interest</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10' }}>{'\u20A6'}{p.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            <button onClick={doInvest} disabled={loading || !amt} data-testid="invest-btn" style={{
              ...btn, marginTop: 12, opacity: (loading || !amt) ? 0.5 : 1,
            }}>
              {loading ? 'Investing...' : 'Invest Now'}
            </button>
          </div>
        )}
        {msg && <div style={{ fontSize: 13, marginTop: 8, color: msg.includes('successful') ? '#10B981' : '#EF4444', fontWeight: 600 }}>{msg}</div>}
      </div>

      {/* Portfolio */}
      {(investments || []).length > 0 && (
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10', marginBottom: 10 }}>Your Portfolio</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {investments.map(i => (
              <div key={i.id} style={{
                background: '#fff', borderRadius: 18, padding: '16px 18px',
                boxShadow: '0 2px 10px rgba(176,49,64,0.06)',
              }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#2D0A10' }}>{i.fund_name}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 8 }}>
                  <span style={{ color: '#A07078' }}>Invested: {'\u20A6'}{i.amount_invested.toLocaleString()}</span>
                  <span style={{ color: '#10B981', fontWeight: 700 }}>Value: {'\u20A6'}{i.current_value.toLocaleString()}</span>
                </div>
                <div style={{ fontSize: 12, color: '#10B981', fontWeight: 600, marginTop: 2 }}>+{'\u20A6'}{i.interest_earned.toLocaleString()} interest</div>
                <button onClick={() => doRedeem(i.id)} data-testid={`redeem-${i.id}`} style={{
                  marginTop: 10, padding: '8px 18px', background: 'rgba(176,49,64,0.06)', border: 'none',
                  borderRadius: 10, color: '#B03140', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                }}>Redeem</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Products */}
      {info.length > 0 && (
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2D0A10', marginBottom: 10 }}>Finance Products</div>
          {info.map(f => (
            <div key={f.name} style={{
              borderRadius: 18, padding: 18, marginBottom: 8,
              background: '#fff', opacity: f.disabled ? 0.5 : 1,
              boxShadow: '0 2px 10px rgba(176,49,64,0.06)',
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#2D0A10' }}>{f.name}</div>
              <div style={{ fontSize: 13, color: '#A07078', marginTop: 2 }}>{f.tagline}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: f.disabled ? '#A07078' : '#B03140', marginTop: 8 }}>{f.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const inp = { width: '100%', padding: '14px 16px', border: '1.5px solid rgba(176,49,64,0.12)', borderRadius: 12, fontSize: 15, outline: 'none', boxSizing: 'border-box', background: 'rgba(176,49,64,0.03)', color: '#2D0A10' };
const btn = { width: '100%', padding: '15px', background: '#B03140', color: '#fff', border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 16px rgba(176,49,64,0.2)' };
